import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AdminHeader, AdminSidebar } from "../components/AdminHeader";
import {
  Plus,
  FolderOpen,
  Clock,
  CheckCircle,
  LogOut,
  Send,
  User,
  Mail,
  FileText,
  TrendingUp,
  Calendar,
  Users,
  Activity,
  Trash2,
  BarChart3,
  Settings,
  AlertCircle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";
const CHART_COLORS = ["#1e3a5f", "#38a169", "#f59e0b", "#e53e3e"];

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [defaultMessageTypes, setDefaultMessageTypes] = useState([]);
  const [newProject, setNewProject] = useState({
    name: "",
    customer_email: "",
    customer_name: "",
    language: "de",
    message_types: [],
  });

  const LANGUAGES = [
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "en", name: "English (UK)", flag: "🇬🇧" },
    { code: "en-US", name: "English (US)", flag: "🇺🇸" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "ar", name: "العربية", flag: "🇪🇬" },
  ];

  useEffect(() => {
    fetchData();
    fetchDefaultMessageTypes();
  }, []);

  const fetchDefaultMessageTypes = async () => {
    try {
      const response = await api.get("/admin/default-message-types");
      setDefaultMessageTypes(response.data);
    } catch (error) {
      console.error("Error fetching default message types:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [projectsRes, statsRes] = await Promise.all([
        api.get("/admin/projects"),
        api.get("/admin/stats"),
      ]);
      setProjects(projectsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(t("admin.errorLoading", "Error loading data"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      // Use default message types if none specified
      const projectData = {
        ...newProject,
        message_types: newProject.message_types.length > 0 ? newProject.message_types : defaultMessageTypes
      };
      await api.post("/admin/projects", projectData);
      toast.success(t("admin.projectCreated", "Project created and invitation sent"));
      setIsDialogOpen(false);
      setNewProject({ name: "", customer_email: "", customer_name: "", language: "de", message_types: [] });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || t("admin.errorCreating", "Error creating");
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleResendInvitation = async (projectId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/admin/projects/${projectId}/resend-invitation`);
      toast.success(t("admin.invitationResent", "Invitation sent again"));
    } catch (error) {
      toast.error(t("admin.errorSendingInvitation", "Error sending invitation"));
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    
    try {
      await api.delete(`/admin/projects/${projectToDelete.id}`);
      toast.success(t("admin.projectDeleted", "Project deleted"));
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || t("admin.errorDeleting", "Error deleting");
      toast.error(message);
    }
  };

  const openDeleteDialog = (project, e) => {
    e.stopPropagation();
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  // Initialize message types from defaults when dialog opens
  const handleOpenDialog = (open) => {
    if (open && newProject.message_types.length === 0 && defaultMessageTypes.length > 0) {
      setNewProject(prev => ({
        ...prev,
        message_types: defaultMessageTypes.map(mt => ({ ...mt }))
      }));
    }
    setIsDialogOpen(open);
  };

  // Add a new message type
  const addMessageType = () => {
    const newKey = `custom_${Date.now()}`;
    setNewProject(prev => ({
      ...prev,
      message_types: [...prev.message_types, { key: newKey, label_de: "", label_en: "", required: false }]
    }));
  };

  // Update a message type
  const updateMessageType = (index, field, value) => {
    setNewProject(prev => ({
      ...prev,
      message_types: prev.message_types.map((mt, i) => 
        i === index ? { ...mt, [field]: value } : mt
      )
    }));
  };

  // Remove a message type
  const removeMessageType = (index) => {
    setNewProject(prev => ({
      ...prev,
      message_types: prev.message_types.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    if (status === "submitted") {
      return (
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          {t("status.completed", "Completed")}
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800">
        <Clock className="w-3 h-3 mr-1" />
        {t("status.pending", "Pending")}
      </Badge>
    );
  };

  const isSuperAdmin = user?.role === "superadmin";

  return (
    <div className="page-bg min-h-screen">
      <AdminSidebar />
      <div className="ml-56">
        <AdminHeader />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-manrope font-bold text-2xl text-foreground">
                {t("admin.projectOverview", "Project Overview")}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t("admin.manageProjects", "Manage your call sequence projects")}
              </p>
            </div>
          <Dialog open={isDialogOpen} onOpenChange={handleOpenDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white" data-testid="create-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t("admin.newProject", "New Project")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope">{t("admin.createNewProject", "Create New Project")}</DialogTitle>
                <DialogDescription>
                  {t("admin.createProjectDesc", "Create a new project and invite a customer.")}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateProject}>
                <Tabs defaultValue="basic" className="mt-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="basic">{t("admin.basicData", "Basic Data")}</TabsTrigger>
                    <TabsTrigger value="messageTypes">{t("admin.messageTypes", "Message Types")}</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="basic" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="projectName" className="label-style">
                        {t("project.name", "Project Name")}
                      </Label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="projectName"
                          placeholder={t("admin.projectNamePlaceholder", "e.g. System Main Street 1")}
                          value={newProject.name}
                          onChange={(e) =>
                            setNewProject({ ...newProject, name: e.target.value })
                          }
                          className="pl-10"
                          required
                          data-testid="project-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="customerName" className="label-style">
                        {t("project.customerName", "Customer Name")}
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="customerName"
                          placeholder={t("admin.customerNamePlaceholder", "John Doe")}
                          value={newProject.customer_name}
                          onChange={(e) =>
                            setNewProject({ ...newProject, customer_name: e.target.value })
                          }
                          className="pl-10"
                          required
                          data-testid="customer-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="customerEmail" className="label-style">
                        {t("admin.customerEmail", "Customer Email")}
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="customerEmail"
                          type="email"
                          placeholder={t("admin.customerEmailPlaceholder", "customer@example.com")}
                          value={newProject.customer_email}
                          onChange={(e) =>
                            setNewProject({ ...newProject, customer_email: e.target.value })
                          }
                          className="pl-10"
                          required
                          data-testid="customer-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="projectLanguage" className="text-slate-700 dark:text-slate-300">
                        {t("admin.languageForInvitation", "Language for invitation & form")}
                      </Label>
                      <Select
                        value={newProject.language}
                        onValueChange={(value) =>
                          setNewProject({ ...newProject, language: value })
                        }
                      >
                        <SelectTrigger data-testid="project-language-select">
                          <SelectValue placeholder={t("admin.selectLanguage", "Select language")} />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES.map((lang) => (
                            <SelectItem key={lang.code} value={lang.code}>
                              {lang.flag} {lang.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="messageTypes" className="space-y-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="label-style">{t("admin.configureMessageTypes", "Configure Message Types")}</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("admin.messageTypesDesc", "Define which message types the customer can select. Required types must be filled.")}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addMessageType}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t("admin.addMessageType", "Add")}
                      </Button>
                    </div>
                    
                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                      {newProject.message_types.map((mt, index) => (
                        <div key={mt.key} className="flex items-start gap-2 p-3 border rounded-lg bg-slate-50 dark:bg-slate-800/50">
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-muted-foreground">🇩🇪 Deutsch</Label>
                              <Input
                                value={mt.label_de}
                                onChange={(e) => updateMessageType(index, "label_de", e.target.value)}
                                placeholder="z.B. Störung Prio 1"
                                className="mt-1 h-8 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">🇬🇧 English</Label>
                              <Input
                                value={mt.label_en}
                                onChange={(e) => updateMessageType(index, "label_en", e.target.value)}
                                placeholder="e.g. Fault Priority 1"
                                className="mt-1 h-8 text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex flex-col items-center gap-2 pt-5">
                            <div className="flex items-center gap-1">
                              <Checkbox
                                id={`required-${index}`}
                                checked={mt.required}
                                onCheckedChange={(checked) => updateMessageType(index, "required", checked)}
                              />
                              <Label htmlFor={`required-${index}`} className="text-xs cursor-pointer">
                                {t("admin.required", "Required")}
                              </Label>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMessageType(index)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {newProject.message_types.length === 0 && (
                        <div className="text-center py-6 text-muted-foreground">
                          <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">{t("admin.noMessageTypes", "No message types defined. Add at least one.")}</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
                
                <DialogFooter className="mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenDialog(false)}
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
                    disabled={isCreating}
                    data-testid="submit-project-btn"
                  >
                    {isCreating ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        {t("common.loading", "Loading...")}
                      </span>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t("admin.createAndInvite", "Create & Invite")}
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="card-base bg-card" data-testid="stats-total">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("stats.total", "Total")}</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.total || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base bg-card" data-testid="stats-pending">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("stats.pending", "Pending")}</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.pending || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base bg-card" data-testid="stats-completed">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("stats.completed", "Completed")}</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.submitted || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base bg-card" data-testid="stats-rate">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("stats.completionRate", "Completion Rate")}</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.completion_rate || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base bg-card" data-testid="stats-week">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("stats.thisWeek", "This Week")}</p>
                  <p className="text-2xl font-bold text-foreground">
                    <span className="text-emerald-600 dark:text-emerald-400">+{stats?.recent_submitted || 0}</span>
                    <span className="text-muted-foreground text-lg"> / </span>
                    <span className="text-blue-600 dark:text-blue-400">+{stats?.recent_created || 0}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{t("stats.completedNew", "completed / new")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        {stats?.monthly_stats && stats.monthly_stats.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Bar Chart */}
            <Card className="card-base">
              <CardHeader className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                <CardTitle className="font-manrope text-lg flex items-center gap-2 text-foreground">
                  <BarChart3 className="w-5 h-5 text-muted-foreground" />
                  {t("charts.projectOverview", "Project Overview (6 Months)")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stats.monthly_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" fontSize={12} tick={{ fill: 'var(--muted-foreground)' }} />
                    <YAxis fontSize={12} tick={{ fill: 'var(--muted-foreground)' }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'var(--card)', 
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }} 
                    />
                    <Legend />
                    <Bar dataKey="created" name={t("charts.created", "Created")} fill="#1e3a5f" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="completed" name={t("charts.completed", "Completed")} fill="#38a169" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            {/* Pie Chart */}
            <Card className="card-base">
              <CardHeader className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                <CardTitle className="font-manrope text-lg flex items-center gap-2 text-foreground">
                  <TrendingUp className="w-5 h-5 text-muted-foreground" />
                  {t("charts.projectStatus", "Project Status")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: t("status.completed", "Completed"), value: stats.submitted || 0 },
                        { name: t("status.pending", "Pending"), value: stats.pending || 0 },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                    >
                      <Cell fill="#38a169" />
                      <Cell fill="#f59e0b" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Projects List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
          </div>
        ) : projects.length === 0 ? (
          <Card className="card-base">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-manrope font-semibold text-lg text-foreground mb-2">
                {t("admin.noProjects", "No projects available")}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t("admin.createFirstProject", "Create your first project to get started.")}
              </p>
              <Button
                className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
                onClick={() => setIsDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("admin.createFirstProjectBtn", "Create First Project")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => (
              <Card
                key={project.id}
                className="card-base card-hover cursor-pointer"
                onClick={() => navigate(`/admin/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-manrope font-semibold text-lg text-foreground truncate">
                          {project.name}
                        </h3>
                        {getStatusBadge(project.status)}
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {project.customer_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {project.customer_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(project.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {project.status === "pending" && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleResendInvitation(project.id, e)}
                            data-testid={`resend-btn-${project.id}`}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            {t("admin.resendInvitation", "Resend invitation")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => openDeleteDialog(project, e)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            data-testid={`delete-btn-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">{t("admin.deleteProject", "Delete Project")}</DialogTitle>
              <DialogDescription>
                {t("admin.deleteProjectConfirm", "Are you sure you want to delete the project")} "{projectToDelete?.name}"? 
                {t("admin.deleteProjectWarning", "This action cannot be undone.")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button 
                onClick={handleDeleteProject}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t("common.delete", "Delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
